import * as Sentry from "@sentry/react";
import { type QueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
	createRootRouteWithContext,
	Outlet,
	useLocation,
	useNavigate,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { ThemeProvider } from "next-themes";
import { useEffect, useState } from "react";
import { Footer, Navbar } from "@/app/partner/components";
import { MobileFlexDrawer } from "@/app/partner/components/MobileDrawer";
import { MobileNavContext } from "@/app/partner/components/MobileNavContext";
import { MobileTabBar } from "@/app/partner/components/MobileTabBar";
import { MobileWaitlistCard } from "@/app/partner/components/MobileWaitlistCard";
import { NoPartnerScreen } from "@/app/partner/components/NoPartnerScreen";
import { Button } from "@/components/ui/button";
import { PageLoader } from "@/components/ui/page-loader";
import { Toaster } from "@/components/ui/sonner";
import { config } from "@/config";
import { useMediaQuery } from "@/hooks";
import { triggerFlexLogin, useFlexAppAuth } from "@/hooks/useFlexAppAuth";
import { isFlexApp } from "@/hooks/useIsFlexApp";
import { GA_EVENTS, gtagEventEmitter } from "@/lib/analytics";
import {
	type Agent,
	agentPartnerQueryOptions,
	agentQueryOptions,
	agentVerificationQueryOptions,
	allPartnersQueryOptions,
	getLoginUrl,
	logoutMutationOptions,
	type Partner,
	type PartnerResponse,
	type User,
	userQueryOptions,
} from "@/lib/auth";
import { getPreferredPartnerId, setPreferredPartnerId } from "@/lib/auth/token";
import {
	isChunkLoadError,
	reloadForChunkError,
	reportChunkLoadError,
} from "@/lib/chunkReload";
import { DialerWidget } from "@/lib/dialer";
import {
	PostHogProvider,
	registerPostHogSuperProperties,
	trackRoutePageView,
} from "@/lib/posthog";
import { safeOpen } from "@/lib/safeOpen";

export interface RouterContext {
	queryClient: QueryClient;
	user: User | null;
	agent: Agent | null;
	partner: Partner | null;
	partners: Partner[];
	hasMultiplePartners: boolean;
	isAgentAccess: boolean;
}

/**
 * Get partner_id from URL search params
 */
/**
 * One sonner setup for every layout branch below.
 *
 * Only placement lives here — the skin is the brand pill from Figma
 * (`toast · opening external` 2168:17581) and is baked into `ui/sonner`, so
 * every branch gets it without repeating the class list. `richColors` stays off
 * for the same reason it always did: it repaints the whole slab per type, which
 * the pill deliberately does not do.
 *
 * `closeButton` is off — it rendered as a detached circle hanging off the
 * card's top-left corner; toasts auto-dismiss and can be swiped away.
 * `mobileOffset` lifts the toast clear of the mobile tab bar, which it used to
 * land on top of.
 */
const TOASTER_PROPS = {
	position: "bottom-right",
	mobileOffset: { bottom: "84px", left: "16px", right: "16px" },
} as const;

function getPartnerIdFromSearch(
	search: Record<string, unknown> | string,
): string | null {
	if (typeof search === "string") {
		const params = new URLSearchParams(search);
		return params.get("partner_id");
	}
	// TanStack Router search is an object
	const partnerId = (search as Record<string, unknown>).partner_id;
	return partnerId ? String(partnerId) : null;
}

export const Route = createRootRouteWithContext<RouterContext>()({
	pendingComponent: () => <PageLoader message="Loading your dashboard..." />,
	errorComponent: ({ error }) => {
		// Show loader for redirect errors (external navigation in progress)
		if (
			error?.message === "REDIRECT_TO_LOGIN" ||
			error?.message === "REDIRECT_TO_FLEX" ||
			error?.message === "REDIRECT_TO_PARTNER"
		) {
			return <PageLoader message="Redirecting..." />;
		}
		// A stale/failed lazy chunk (app opened before a deploy, or a flaky
		// cold-start fetch) surfaces here as a dynamic-import error. Reload once
		// to pick up the fresh index.html + chunk hashes — the manual restart,
		// made automatic. Guarded so a broken deploy can't loop.
		if (isChunkLoadError(error)) {
			// Always report, with what the browser received for each chunk, so we
			// can see WHICH chunk failed and HOW (404 vs HTML 2xx) even when the
			// reload below hides it from the user.
			reportChunkLoadError(error, "root-errorComponent");
			if (reloadForChunkError()) {
				return <PageLoader message="Updating…" />;
			}
		} else {
			Sentry.captureException(error);
		}
		// Generic error state. The app gets a real refresh action; web keeps its
		// original text-only screen (no impact on web).
		console.error("[Root ErrorBoundary]", error);
		return (
			<div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
				<p className="text-gray-500 dark:text-zinc-400">
					Something went wrong. Please refresh.
				</p>
				<p className="max-w-lg text-xs text-gray-400 dark:text-zinc-500 break-all">
					{error instanceof Error ? error.message : String(error)}
				</p>
				{isFlexApp() && (
					<Button
						onClick={() => window.location.reload()}
						className="h-11 px-6"
					>
						Refresh
					</Button>
				)}
			</div>
		);
	},
	beforeLoad: async ({ context, location }) => {
		// Redirect /flex/login to /partner
		if (
			location.pathname === "/flex/login" ||
			location.pathname === "/flex/login/"
		) {
			window.location.replace("/partner");
			throw new Error("REDIRECT_TO_PARTNER");
		}

		// Redirect /partners to /partner
		if (
			location.pathname === "/partners" ||
			location.pathname === "/partners/"
		) {
			window.location.replace("/partner");
			throw new Error("REDIRECT_TO_PARTNER");
		}

		// Redirect /partners/* to /flex/*
		if (location.pathname.startsWith("/partners/")) {
			const newPath = location.pathname.replace(/^\/partners/, "/flex");
			const searchString = location.searchStr || "";
			window.location.replace(`${newPath}${searchString}`);
			throw new Error("REDIRECT_TO_FLEX");
		}

		// Redirect /partner/* to /flex/* (but not /partner itself)
		if (
			location.pathname.startsWith("/partner/") &&
			location.pathname !== "/partner/"
		) {
			const newPath = location.pathname.replace(/^\/partner/, "/flex");
			const searchString = location.searchStr || "";
			window.location.replace(`${newPath}${searchString}`);
			throw new Error("REDIRECT_TO_FLEX");
		}

		// Skip auth for public partner page
		if (location.pathname === "/partner") {
			return {
				user: null,
				agent: null,
				partner: null,
				partners: [],
				hasMultiplePartners: false,
				isAgentAccess: false,
			};
		}

		// Skip auth for /connect and all /connect/* routes (frontend-only, no backend yet)
		if (
			location.pathname === "/connect" ||
			location.pathname.startsWith("/connect/")
		) {
			return {
				user: null,
				agent: null,
				partner: null,
				partners: [],
				hasMultiplePartners: false,
				isAgentAccess: false,
			};
		}

		// Skip auth for sharing link redirect routes (/flex/:id)
		// These are public routes that redirect to the global website
		// The regex matches /flex/{id} but not /flex/dashboard or /flex/dashboard/*
		const isFlexSharingLink =
			/^\/flex\/[^/]+$/.test(location.pathname) &&
			location.pathname !== "/flex/dashboard";
		if (isFlexSharingLink) {
			return {
				user: null,
				agent: null,
				partner: null,
				partners: [],
				hasMultiplePartners: false,
				isAgentAccess: false,
			};
		}

		// Check if this is an agent access flow (partner_id in URL)
		const partnerIdFromUrl = getPartnerIdFromSearch(location.search);
		const isAgentAccess = !!partnerIdFromUrl;

		try {
			if (isAgentAccess) {
				// AGENT ACCESS FLOW
				// 1. Verify user is logged in
				const user =
					await context.queryClient.ensureQueryData(userQueryOptions);

				// 2. Verify user is an internal agent
				let agent: Agent | null = null;
				try {
					agent = await context.queryClient.ensureQueryData(
						agentVerificationQueryOptions,
					);
				} catch {
					// Not an agent - redirect to login with next URL
					console.error("Agent verification failed - not an internal agent");
					const loginUrl = getLoginUrl();
					const nextUrl = encodeURIComponent(
						window.location.pathname + window.location.search,
					);
					window.location.href = `${loginUrl}?next=${nextUrl}`;
					throw new Error("REDIRECT_TO_LOGIN");
				}

				// 3. Fetch partner data using agent endpoint
				let partner: Partner | null = null;
				try {
					partner = await context.queryClient.ensureQueryData(
						agentPartnerQueryOptions(partnerIdFromUrl),
					);
				} catch {
					// Partner not found - redirect to home
					console.error("Failed to fetch partner for agent access");
					window.location.href = "/flex/";
					throw new Error("REDIRECT_TO_LOGIN");
				}

				return {
					user,
					agent,
					partner,
					partners: partner ? [partner] : [],
					hasMultiplePartners: false,
					isAgentAccess: true,
				};
			}

			// NORMAL PARTNER FLOW
			// First check user auth - fail fast if not authenticated
			// This prevents multiple 401 requests when session is expired
			const user = await context.queryClient.ensureQueryData(userQueryOptions);

			// Only fetch agent and partners after confirming user is authenticated
			const [agent, partnersResponse] = await Promise.all([
				context.queryClient
					.ensureQueryData(agentQueryOptions)
					.catch(() => null),
				context.queryClient
					.ensureQueryData(allPartnersQueryOptions)
					.catch(() => ({ result: [] }) as PartnerResponse),
			]);

			const partners = partnersResponse?.result || [];
			const preferredPartner = getPreferredPartnerId();

			// Flex app only: authenticated, but this account has no partner. Resolve it
			// HERE, before the matched child-route guards run. Each dashboard guard
			// rejects a null partner and redirects to a sibling (/flex/dashboard ⇄
			// /flex/dashboard/leads); with no partner that is an infinite redirect loop
			// that never resolves the beforeLoad chain, so nothing ever renders and the
			// app shows a blank screen.
			//
			// We send them to /partner with a full-document navigation — the SAME
			// deterministic mechanism the web path already uses below. /partner then
			// renders the NoPartnerScreen for the app (see routes/partner.tsx). The
			// pathname guard stops this from firing again once we're on /partner, which
			// would be a reload loop. Web is intentionally left untouched.
			if (
				partners.length === 0 &&
				!isAgentAccess &&
				isFlexApp() &&
				location.pathname !== "/partner"
			) {
				window.location.replace("/partner");
				throw new Error("REDIRECT_TO_PARTNER");
			}

			// Determine current partner
			let partner: Partner | null = null;
			if (partners.length > 0) {
				if (preferredPartner) {
					partner =
						partners.find((p) => `${p.partner_id}` === preferredPartner) ||
						partners[0];
				} else if (partners.length === 1) {
					// Auto-set for single partner
					partner = partners[0];
					setPreferredPartnerId(partner.partner_id.toString());
				}
			}

			return {
				user,
				agent,
				partner,
				partners,
				hasMultiplePartners: partners.length > 1,
				isAgentAccess: false,
			};
		} catch (error) {
			// Re-throw intentional redirect errors so the errorComponent shows
			// the correct "Redirecting…" loader instead of "Something went wrong"
			if (
				error instanceof Error &&
				(error.message === "REDIRECT_TO_LOGIN" ||
					error.message === "REDIRECT_TO_FLEX" ||
					error.message === "REDIRECT_TO_PARTNER")
			) {
				throw error;
			}

			console.error("Auth fetch failed:", error);
			// In Flex app, trigger native login sheet instead of web redirect
			if (isFlexApp()) {
				triggerFlexLogin();
				throw new Error("REDIRECT_TO_LOGIN");
			}
			const baseUrl = config.urls.webServiceUrl.replace(/\/+$/, "");
			const partnerUrl = `${baseUrl}/partner`;
			if (typeof window !== "undefined") {
				window.location.replace(partnerUrl);
			}
			throw new Error("REDIRECT_TO_LOGIN");
		}
	},
	component: RootComponentWrapper,
});

/**
 * Wraps RootComponent with PostHogProvider so PostHog is initialized once
 * at the top of the tree, regardless of which route branch renders.
 */
function RootComponentWrapper() {
	return (
		<PostHogProvider>
			<RootComponent />
		</PostHogProvider>
	);
}

function RootComponent() {
	const { user, partners, isAgentAccess, hasMultiplePartners } =
		Route.useRouteContext();
	const location = useLocation();

	// Listen for auth token from Flex native app (global, works on all pages)
	useFlexAppAuth();

	useEffect(() => {
		const isPartnerPage = location.pathname === "/partner";
		const isConnect = location.pathname.startsWith("/connect");
		document.title = isPartnerPage
			? "Partner With Us"
			: isConnect
				? "Amber Connect"
				: "Amber Flex";
	}, [location.pathname]);

	// Track page views on route change and re-register super properties
	useEffect(() => {
		registerPostHogSuperProperties();

		// Derive screen name from pathname
		const segments = location.pathname.split("/").filter(Boolean);
		const screen = segments[segments.length - 1] || "home";
		trackRoutePageView(screen);
	}, [location.pathname]);

	// Public partner page - render without auth layout
	if (location.pathname === "/partner") {
		return (
			<ThemeProvider
				attribute="class"
				defaultTheme="light"
				forcedTheme="light"
				enableSystem={false}
			>
				<Outlet />
				<Toaster {...TOASTER_PROPS} />
			</ThemeProvider>
		);
	}

	// Public connect surface - render without auth layout (frontend-only for now)
	if (
		location.pathname === "/connect" ||
		location.pathname.startsWith("/connect/")
	) {
		return (
			<ThemeProvider
				attribute="class"
				defaultTheme="light"
				forcedTheme="light"
				enableSystem={false}
			>
				<Outlet />
				<Toaster {...TOASTER_PROPS} />
			</ThemeProvider>
		);
	}

	// Sharing link redirect routes (/flex/:id) - render without auth layout
	const isFlexSharingLink =
		/^\/flex\/[^/]+$/.test(location.pathname) &&
		location.pathname !== "/flex/dashboard";
	if (isFlexSharingLink) {
		return (
			<ThemeProvider
				attribute="class"
				defaultTheme="light"
				forcedTheme="light"
				enableSystem={false}
			>
				<Outlet />
			</ThemeProvider>
		);
	}

	// If not authenticated, show loader (redirect already initiated in beforeLoad)
	if (!user) {
		return <PageLoader message="Redirecting to login..." />;
	}

	// If no partners at all (and not agent access), send them to /partner.
	// The Flex app is normally redirected here in beforeLoad and shown the
	// NoPartnerScreen on /partner; this is a defensive fallback for both.
	if (partners.length === 0 && !isAgentAccess) {
		if (isFlexApp()) {
			return (
				<ThemeProvider
					attribute="class"
					defaultTheme="light"
					forcedTheme="light"
					enableSystem={false}
				>
					<NoPartnerScreen email={user.email} />
				</ThemeProvider>
			);
		}
		window.location.replace("/partner");
		return <PageLoader message="Redirecting..." />;
	}

	// For partner selection page with multiple partners, render without sidebar
	const isSelectPage = location.pathname === "/flex/dashboard/select";
	const preferredPartner = getPreferredPartnerId();
	if (isSelectPage && hasMultiplePartners && !preferredPartner) {
		return <SelectPageLayout user={user} />;
	}

	return <AuthenticatedLayout user={user} />;
}

function SelectPageLayout({ user: _user }: { user: User }) {
	return (
		<ThemeProvider
			attribute="class"
			defaultTheme="light"
			forcedTheme="light"
			enableSystem={false}
		>
			<div className="relative min-h-screen">
				{/* Background with sidebar preview (blurred/dimmed) */}
				<div className="absolute inset-0 bg-gray-100 dark:bg-zinc-800">
					<div className="flex h-full">
						{/* Fake sidebar placeholder */}
						<div className="w-64 bg-white dark:bg-zinc-900 border-r border-gray-200 dark:border-zinc-800" />
						{/* Fake content area */}
						<div className="flex-1" />
					</div>
				</div>
				{/* Dark overlay */}
				<div className="absolute inset-0 bg-black/60 z-40" />
				{/* Modal content */}
				<div className="relative z-50">
					<Outlet />
				</div>
			</div>
			<Toaster {...TOASTER_PROPS} />
			<ReactQueryDevtools buttonPosition="top-right" />
			<TanStackRouterDevtools position="bottom-right" />
		</ThemeProvider>
	);
}

function AuthenticatedLayout({ user }: { user: User }) {
	// Use regular query instead of suspense - agent query will fail for non-agent users
	const { data: agent } = useQuery({
		...agentQueryOptions,
		retry: false,
	});
	const { partner, isAgentAccess } = Route.useRouteContext();
	const location = useLocation();
	const navigate = useNavigate();
	const logoutMutation = useMutation(logoutMutationOptions);
	const [navCollapsed, setNavCollapsed] = useState(false);

	// Mobile waitlist card persistence
	// localStorage = permanent dismiss (close X, join waitlist)
	// sessionStorage = session-only dismiss (maybe later — clears on new login/session)
	const WAITLIST_STORAGE_KEY = "flex_mobile_waitlist_dismissed";
	const WAITLIST_SESSION_KEY = "flex_mobile_waitlist_maybe_later";
	const [waitlistDismissed, setWaitlistDismissed] = useState(() => {
		try {
			return (
				localStorage.getItem(WAITLIST_STORAGE_KEY) === "true" ||
				sessionStorage.getItem(WAITLIST_SESSION_KEY) === "true"
			);
		} catch {
			return false;
		}
	});

	const showWaitlistCard =
		!waitlistDismissed &&
		location.pathname.startsWith("/flex/dashboard") &&
		!location.pathname.includes("/settings") &&
		!location.pathname.includes("/custom-form") &&
		!location.pathname.includes("/custom-page") &&
		location.pathname !== "/flex/dashboard/select";

	const handleWaitlistJoin = () => {
		gtagEventEmitter({
			action: GA_EVENTS.ACTION.MOBILE_WAITLIST_JOIN,
			category: GA_EVENTS.CATEGORY,
			label: GA_EVENTS.LABEL.MOBILE_WAITLIST,
			partner_id: partner?.partner_id,
			email: user.email,
			name: user.name,
		});

		// Persist so the banner stays hidden after page reload
		try {
			localStorage.setItem(WAITLIST_STORAGE_KEY, "true");
		} catch {}
	};

	const handleWaitlistDismiss = (reason: "close" | "maybe_later") => {
		const action =
			reason === "close"
				? GA_EVENTS.ACTION.MOBILE_WAITLIST_CLOSE
				: GA_EVENTS.ACTION.MOBILE_WAITLIST_DISMISS;

		gtagEventEmitter({
			action,
			category: GA_EVENTS.CATEGORY,
			label: GA_EVENTS.LABEL.MOBILE_WAITLIST,
			partner_id: partner?.partner_id,
			email: user.email,
			name: user.name,
		});

		// Only persist dismissal for close (X button), not for "Maybe later"
		if (reason === "close") {
			try {
				localStorage.setItem(WAITLIST_STORAGE_KEY, "true");
			} catch {}
		} else {
			// "Maybe later" — persist for this session only
			try {
				sessionStorage.setItem(WAITLIST_SESSION_KEY, "true");
			} catch {}
		}
		setWaitlistDismissed(true);
	};

	const handleWaitlistClose = () => {
		try {
			localStorage.setItem(WAITLIST_STORAGE_KEY, "true");
		} catch {}
		setWaitlistDismissed(true);
	};

	const isMobile = useMediaQuery("(max-width: 1023px)");
	const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

	// Auto-close drawer when transitioning back to desktop
	useEffect(() => {
		if (!isMobile && mobileDrawerOpen) {
			setMobileDrawerOpen(false);
		}
	}, [isMobile, mobileDrawerOpen]);

	const showFooter =
		!location.pathname.includes("/integrations") &&
		!location.pathname.includes("/support") &&
		!location.pathname.includes("/listings") &&
		!location.pathname.includes("/campaigns") &&
		!location.pathname.includes("/subpartners") &&
		!location.pathname.includes("/leads") &&
		!location.pathname.includes("/bookings") &&
		!location.pathname.includes("/settings") &&
		!location.pathname.includes("/teams") &&
		location.pathname !== "/flex/dashboard" &&
		location.pathname !== "/flex/dashboard/";

	// Build partner nav config from partner data
	// For agent access: agents have full access (isAuthorized = true)
	const partnerConfig = partner
		? {
				navbarConfig: partner.partner_details?.navbar_config || [],
				isSubPartner: isAgentAccess
					? Boolean(partner.partner?.parent_id)
					: Boolean(partner.parent_details?.id),
				isAuthorized: isAgentAccess
					? true
					: !partner?.set_profile_active?.[3]?.meta?.role ||
						partner?.set_profile_active?.[3]?.meta?.role === "admin",
				isBookingPartner: isAgentAccess
					? (partner as any)?.partner_details?.is_booking_partner ||
						(partner as any)?.data?.is_booking_partner ||
						false
					: (partner as any)?.partner_details?.is_booking_partner ||
						(partner as any)?.data?.is_booking_partner ||
						(partner as any)?.parent_details?.is_booking_partner ||
						(partner as any)?.partner_details?.data?.is_booking_partner ||
						false,
				isPartnerCustomFormEnabled:
					partner.partner_details?.is_partner_custom_form_enabled || false,
				partnerName:
					partner.partner_details?.name ||
					partner.partner?.name ||
					partner.name,
			}
		: undefined;

	const handleMobileNavigate = (href: string, internal?: boolean) => {
		const searchParams = new URLSearchParams(window.location.search);
		const partnerIdFromUrl = searchParams.get("partner_id");

		if (internal) {
			if (partnerIdFromUrl) {
				window.location.href = `/flex${href}?partner_id=${partnerIdFromUrl}`;
			} else {
				navigate({ to: `/flex${href}` });
			}
		} else {
			const separator = href.includes("?") ? "&" : "?";
			const url = partnerIdFromUrl
				? `${href}${separator}partner_id=${partnerIdFromUrl}`
				: href;
			safeOpen(url);
		}
	};

	return (
		<ThemeProvider
			attribute="class"
			defaultTheme="light"
			forcedTheme="light"
			enableSystem={false}
		>
			<div className="flex h-dvh">
				{/* Desktop sidebar — hidden below lg (requirement 1.1, 1.7) */}
				<div className={isMobile ? "hidden" : undefined}>
					<Navbar
						user={{
							name: user.name,
							email: user.email,
						}}
						partnerConfig={partnerConfig}
						onLogout={() => {
							logoutMutation.mutate();
						}}
						isCollapsed={navCollapsed}
						onToggleCollapse={() => setNavCollapsed(!navCollapsed)}
					/>
				</div>

				<div className="flex-1 flex flex-col overflow-hidden">
					{/* Mobile sticky top bar removed — hamburger is now inside each page's Header component */}

					<main className="flex-1 h-full overflow-hidden">
						<MobileNavContext.Provider
							value={{ openDrawer: () => setMobileDrawerOpen(true) }}
						>
							<Outlet />
						</MobileNavContext.Provider>
					</main>
					{showFooter && (
						<Footer
							currentPage={1}
							totalPages={1}
							totalEntries={1}
							entriesPerPage={10}
						/>
					)}
					{/* Bottom tab bar — primary mobile navigation */}
					{isMobile && (
						<MobileTabBar
							pathname={location.pathname}
							navbarConfig={partnerConfig?.navbarConfig ?? []}
							isSubPartner={partnerConfig?.isSubPartner ?? false}
							isAuthorized={partnerConfig?.isAuthorized ?? true}
							isBookingPartner={partnerConfig?.isBookingPartner ?? false}
							onNavigate={handleMobileNavigate}
							onOpenMore={() => setMobileDrawerOpen(true)}
						/>
					)}
				</div>

				{/* Mobile waitlist card — desktop only, excluded from settings pages */}
				{showWaitlistCard && (
					<div className="hidden lg:block">
						<MobileWaitlistCard
							email={user.email}
							agentEmail={(partner as any)?.agent_details?.email || undefined}
							onJoinWaitlist={handleWaitlistJoin}
							onDismiss={handleWaitlistDismiss}
							onClose={handleWaitlistClose}
						/>
					</div>
				)}

				{/* Mobile nav drawer (requirement 1.2, 1.3, 1.4, 1.5, 1.8) */}
				{isMobile && (
					<MobileFlexDrawer
						open={mobileDrawerOpen}
						onOpenChange={setMobileDrawerOpen}
						onNavigate={handleMobileNavigate}
						pathname={location.pathname}
						user={{
							name: user.name,
							email: user.email,
						}}
						partnerConfig={partnerConfig}
						onLogout={() => {
							logoutMutation.mutate();
						}}
					/>
				)}

				<Toaster {...TOASTER_PROPS} />
				{agent && <DialerWidget agent={agent} />}
			</div>
			<ReactQueryDevtools buttonPosition="top-right" />
			<TanStackRouterDevtools position="bottom-right" />
		</ThemeProvider>
	);
}
